/**
 * Support hub — DSS documentation index (no marketing hero / logo parade).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from '@mui/material';
// Paper used for tables and contact
import {
  Science,
  WaterDrop,
  Analytics,
  TrendingUp,
  VideoLibrary,
  ArrowForward,
} from '@mui/icons-material';
import { StructuredData, BreadcrumbSEO, HowToSEO, HOWTO_GUIDES } from '../SEO';
import SupportShell from './SupportShell';

const GUIDES = [
  {
    title: 'Agronomic principles',
    description: 'Nutrient uptake, pH, EC, and plant physiology basics.',
    level: 'Foundation',
    path: '/support/agronomic-principles',
    icon: <Science fontSize="small" color="primary" />,
  },
  {
    title: 'Water quality',
    description: 'Source water ions, testing, and how water enters the solve.',
    level: 'Foundation',
    path: '/support/water-quality-management',
    icon: <WaterDrop fontSize="small" color="primary" />,
  },
  {
    title: 'Advanced calculations',
    description: 'Matrix solve, units, DOF, concentration factor, stock dilution.',
    level: 'Core',
    path: '/support/advanced-calculations',
    icon: <Analytics fontSize="small" color="primary" />,
  },
  {
    title: 'Yield optimization',
    description: 'Scheduling and operational practices around the recipe.',
    level: 'Ops',
    path: '/support/yield-optimization',
    icon: <TrendingUp fontSize="small" color="primary" />,
  },
  {
    title: 'Video tutorials',
    description: 'Walkthrough media (when published).',
    level: 'Media',
    path: '/support/video-tutorials',
    icon: <VideoLibrary fontSize="small" color="primary" />,
  },
];

const PIPELINE = [
  { step: '1', name: 'Water', path: '/water-quality', note: 'Source ions, pH, EC' },
  { step: '2', name: 'Crop', path: '/formulation', note: 'Target setpoints' },
  { step: '3', name: 'Salts', path: '/salt-library', note: 'Fertilizer selection' },
  { step: '4', name: 'Recipe', path: '/stock-solutions', note: 'Parameters → solve → verify' },
  { step: '5', name: 'Results', path: '/results', note: 'Report, PDF, prep' },
];

const SupportPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <SupportShell
      title="Support & documentation"
      subtitle="Guides for the FlahaFast nutrient DSS — pipeline, chemistry, and operations."
      backTo="/"
      backLabel="Home"
    >
      <StructuredData type="faq" />
      <StructuredData type="contactpage" />
      <HowToSEO {...HOWTO_GUIDES.nutrientCalculation} />
      <BreadcrumbSEO />

      <Alert severity="info" variant="outlined" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Primary product path:{' '}
          <strong>Water → Crop → Salts → Recipe → Results</strong>. Open Workspace after
          sign-in to run a calculation.
        </Typography>
      </Alert>

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Recipe pipeline
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={48}>#</TableCell>
              <TableCell>Stage</TableCell>
              <TableCell>Purpose</TableCell>
              <TableCell width={100} align="right">
                Open
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {PIPELINE.map((p) => (
              <TableRow key={p.step} hover>
                <TableCell>{p.step}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {p.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {p.note}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
                    onClick={() => navigate(p.path)}
                  >
                    Go
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Guides
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>Topic</TableCell>
              <TableCell>Summary</TableCell>
              <TableCell width={100}>Level</TableCell>
              <TableCell width={100} align="right">
                Open
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {GUIDES.map((g) => (
              <TableRow
                key={g.path}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(g.path)}
              >
                <TableCell>{g.icon}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {g.title}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {g.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={g.level} size="small" variant="outlined" />
                </TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <Button size="small" onClick={() => navigate(g.path)}>
                    Read
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Contact
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Product and enterprise inquiries: use the site contact or email your Flaha
          account representative. For calculation math, see Advanced calculations and the
          in-app Verify tab on Results.
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="contained" onClick={() => navigate('/login')}>
            Sign in
          </Button>
          <Button size="small" variant="outlined" onClick={() => navigate('/')}>
            Home
          </Button>
        </Stack>
      </Paper>
    </SupportShell>
  );
};

export default SupportPage;
